import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

interface PipeDiagramProps {
  distance: number[];
  pipeLengthValue: number;
  view: "detected" | "extracted" | "combined";
  selectedIndex?: number | null;
  mainCodes: string[];
  mainCodeColors: { mainCode: string; color: string }[]; 
}

const PipeDiagram: React.FC<PipeDiagramProps> = ({ distance, pipeLengthValue, mainCodes, mainCodeColors, selectedIndex, view }) => {
  const ref = useRef<SVGSVGElement | null>(null);

  const pipeElements = distance.map((d, i) => ({
    distance: d,
    mainCode: mainCodes[i],
  }));

  const sortedData = [...pipeElements].sort((a, b) => a.distance - b.distance);
  const pipeLength = pipeLengthValue ?? Math.max(...distance, 50);
  const pipeTopPadding = 20;
  const rowCount = view === "combined" ? distance.length * 1.8 : distance.length;
  const pipeHeight = Math.max(rowCount * 64, 300);
  const totalHeight = pipeHeight + pipeTopPadding * 2;

  const yScale = d3
  .scaleLinear()
  .domain([0, pipeLength])
  .range([pipeTopPadding, pipeTopPadding + pipeHeight]);

  useEffect(() => {
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();
    const circleRadius = 15;
    const minY = pipeTopPadding + circleRadius;
    const maxY = pipeTopPadding + pipeHeight - circleRadius;

    const getRowCenterY = (index: number): number => {
      const row = document.querySelector(`.row-${index}`);
      if (!row) return 0;

      const rect = row.getBoundingClientRect();
      const svgRect = ref.current?.getBoundingClientRect();
      if (!svgRect) return 0;

      return rect.top - svgRect.top + rect.height / 2;
  };

    const pipeX = 60;
    const pipeWidth = 20;
    svg
      .append("rect")
      .attr("x", pipeX - pipeWidth / 2)
      .attr("y", pipeTopPadding)
      .attr("width", pipeWidth)
      .attr("height", pipeHeight)
      .attr("fill", "#88aacc")
      .attr("rx", 10);

    svg
      .append("circle")
      .attr("cx", 60)
      .attr("cy", pipeTopPadding)
      .attr("r", 15)
      .attr("fill", "#88aacc");

    svg
      .append("circle")
      .attr("cx", pipeX)
      .attr("cy", pipeTopPadding + pipeHeight)
      .attr("r", 15)
      .attr("fill", "#88aacc");

    svg
      .append("defs")
      .append("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 0 10 10")
      .attr("refX", 10)
      .attr("refY", 5)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M 0 0 L 10 5 L 0 10 z")
      .attr("fill", "context-stroke");

      const start = selectedIndex != null && selectedIndex < distance.length ? distance[selectedIndex] : null;
      const end = selectedIndex != null && selectedIndex + 1 < distance.length ? distance[selectedIndex + 1] : pipeLength;

      if (start !== null && end !== null) {
        let yStart = yScale(start); 
        let yEnd = yScale(end);

        yStart = Math.max(minY, Math.min(maxY, yStart));
        yEnd = Math.max(minY, Math.min(maxY, yEnd));

        if (yStart === yEnd) {
          svg
            .append("line")
            .attr("x1", pipeX - pipeWidth / 2)
            .attr("y1", yStart)
            .attr("x2", pipeX + pipeWidth / 2)
            .attr("y2", yEnd)
            .attr("stroke", "gray")
            .attr("stroke-width", 3);
        }else{
          const yTop = Math.min(yStart, yEnd);
          const yBottom = Math.max(yStart, yEnd);
          svg
            .append("rect")
            .attr("x", pipeX - pipeWidth / 2)
            .attr("y", yTop)
            .attr("width", pipeWidth)
            .attr("height", yBottom - yTop)
            .attr("fill", "gray");
        } 
    }

    setTimeout(() => {
      sortedData.forEach((item, index) => {
        let startY = yScale(item.distance);

        const circleRadius = 15;
        const minY = pipeTopPadding + circleRadius;
        const maxY = pipeTopPadding + pipeHeight - circleRadius;
        startY = Math.max(minY, Math.min(maxY, startY));

        const rowIndex = view === "combined" ? index * 2  : index;
        const endY = Math.round(getRowCenterY(rowIndex));
        const startX = pipeX + pipeWidth / 2;
        const endX = 250;
        const bendX = 100;

        const color = mainCodeColors.find(c => c.mainCode === item.mainCode)?.color || "gray";

        svg
          .append("line")
          .attr("x1", Math.round(startX))
          .attr("y1", Math.round(startY))
          .attr("x2", Math.round(bendX))
          .attr("y2", Math.round(startY))
          .attr("stroke", color)
          .attr("stroke-width", 1.5);

        svg
          .append("line")
          .attr("x1", Math.round(bendX))
          .attr("y1", Math.round(startY))
          .attr("x2", Math.round(endX))
          .attr("y2", Math.round(endY))
          .attr("stroke", color)
          .attr("stroke-width", 1.5)
          .attr("marker-end", "url(#arrow)");
      });
    }, 50);
  }, [distance, selectedIndex, pipeLengthValue, mainCodes, mainCodeColors]);

  return <svg ref={ref} width={250} height={totalHeight} />;
};

export default PipeDiagram;